import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { getModuleKeyForPath } from '../config/permissionModules';

interface PermissionRouteProps {
  children: React.ReactNode;
  /** Override module key; defaults to path-based lookup */
  moduleKey?: string;
}

/**
 * Auth + module permission gate. Use inside Router after AuthProvider.
 * Paths without a mapped module key only require authentication.
 */
const PermissionRoute: React.FC<PermissionRouteProps> = ({ children, moduleKey }) => {
  const { colors } = useTheme();
  const { t } = useTranslation('common');
  const location = useLocation();
  const { isAuthenticated, isLoading, permissionsLoaded, can } = useAuth();

  const resolvedKey = moduleKey ?? getModuleKeyForPath(location.pathname);

  if (isLoading || (isAuthenticated && !permissionsLoaded)) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className={colors.textSecondary}>{t('states.loading', { defaultValue: 'Loading...' })}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  if (resolvedKey && !can(resolvedKey, 'view')) {
    return <Navigate to="/forbidden" replace />;
  }

  return <>{children}</>;
};

export default PermissionRoute;
